/**
 * TanStack Query client configuration
 *
 * Centralized QueryClient instance with sensible defaults
 * for data fetching, caching, and error handling.
 *
 * ## OTel integration (E3)
 *
 * QueryCache and MutationCache `onError` callbacks create short-lived
 * OTel spans so that failed queries/mutations surface in traces.
 *
 * ### Cardinality notes
 *
 * - We do **not** serialize the full query key (could be large and high-cardinality).
 * - If the query/mutation provides `meta.operationName`, we record it as a
 *   low-cardinality attribute; otherwise we omit it.
 * - Only `exception.type` and `exception.message` are recorded (PII-safe).
 */

import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { SpanStatusCode } from "@opentelemetry/api";
import { getTracer } from "@/telemetry";

/** Time constants for cache configuration */
const MINUTE = 1000 * 60;

// ---------------------------------------------------------------------------
// OTel error recording helpers
// ---------------------------------------------------------------------------

/**
 * Record a query or mutation failure as an OTel span.
 *
 * @param spanName - e.g. "query.error" or "mutation.error"
 * @param error    - the thrown error
 * @param meta     - optional query/mutation meta object
 */
function recordQueryError(
  spanName: string,
  error: Error,
  meta?: Record<string, unknown>,
): void {
  const tracer = getTracer("floline-web.react-query");
  const span = tracer.startSpan(spanName);

  const errName = error.constructor.name;
  const errMessage = error.message;

  const attrs: Record<string, string> = {
    "exception.type": errName,
    "exception.message": errMessage,
  };

  // Only record operationName if provided by the consumer — keeps cardinality low.
  const operationName = meta?.operationName;
  if (typeof operationName === "string" && operationName) {
    attrs["rq.operation_name"] = operationName;
  }

  span.setAttributes(attrs);
  span.setStatus({ code: SpanStatusCode.ERROR, message: errMessage });
  span.recordException({ name: errName, message: errMessage });
  span.end();
}

// ---------------------------------------------------------------------------
// Caches with OTel hooks
// ---------------------------------------------------------------------------

function createQueryCache(): QueryCache {
  return new QueryCache({
    onError(error, query) {
      recordQueryError("rq.query.error", error, query.options.meta as Record<string, unknown> | undefined);
    },
  });
}

function createMutationCache(): MutationCache {
  return new MutationCache({
    onError(error, _variables, _context, mutation) {
      recordQueryError("rq.mutation.error", error, mutation.options.meta as Record<string, unknown> | undefined);
    },
  });
}

// ---------------------------------------------------------------------------
// Default options
// ---------------------------------------------------------------------------

/**
 * Default query options for optimal UX:
 * - staleTime: Data considered fresh for 1 minute (reduces unnecessary refetches)
 * - gcTime: Unused data garbage collected after 5 minutes
 * - retry: Retry failed requests up to 3 times with exponential backoff
 * - refetchOnWindowFocus: Refetch stale data when window regains focus
 */
const defaultQueryOptions = {
  queries: {
    staleTime: 1 * MINUTE,
    gcTime: 5 * MINUTE,
    retry: 3,
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: true,
  },
};

// ---------------------------------------------------------------------------
// Factory + singleton
// ---------------------------------------------------------------------------

/**
 * Create a new QueryClient instance
 *
 * Use this factory function to create instances for SSR
 * or testing scenarios where isolated clients are needed.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: createQueryCache(),
    mutationCache: createMutationCache(),
    defaultOptions: defaultQueryOptions,
  });
}

/**
 * Singleton QueryClient instance for client-side use
 *
 * This instance is shared across the application for
 * consistent cache management and query deduplication.
 */
export const queryClient = createQueryClient();
